// Client self-serve invoices + statement. Download any order's invoice PDF or a full account
// statement (outstanding + order history).
import { useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader.jsx';
import StatusBadge from '../components/StatusBadge.jsx';
import { listOrdersPage, invoiceUrl } from '../api/orders.js';
import Pager from '../components/Pager.jsx';
import { myStatementUrl } from '../api/companies.js';
import { fmtOrderTotal } from '../lib/matrix.js';

export default function ClientInvoices() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 50;

  useEffect(() => {
    setLoading(true);
    listOrdersPage({ page, pageSize: PAGE_SIZE })
      .then((r) => {
        setOrders(r.orders);
        setTotal(r.total);
      })
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  }, [page]);

  return (
    <>
      <PageHeader
        title="Invoices & statements"
        subtitle="Download individual invoices or a full account statement."
        actions={
          <a
            href={myStatementUrl()}
            target="_blank"
            rel="noreferrer"
            className="rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-hover"
          >
            Download statement
          </a>
        }
      />

      {loading ? (
        <div className="text-sm text-text-muted">Loading…</div>
      ) : orders.length === 0 ? (
        <div className="rounded-lg border border-dashed border-line bg-surface p-10 text-center text-sm text-text-muted">
          No orders yet.
        </div>
      ) : (
        <>
        <ul className="divide-y divide-line rounded-lg border border-line bg-surface shadow-sm md:hidden">
          {orders.map((o) => (
            <li key={o.id} className="flex items-center gap-3 px-3 py-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-mono text-xs text-text-secondary">{o.orderNumber || o.id.slice(-8)}</span>
                  <span className="text-sm font-semibold tabular-nums">{fmtOrderTotal(o)}</span>
                </div>
                <div className="mt-1 flex items-center gap-1.5 text-[11px] text-text-muted">
                  <StatusBadge status={o.status} />
                  <span>{new Date(o.createdAt).toLocaleDateString()}</span>
                </div>
              </div>
              <a
                href={invoiceUrl(o.id)}
                target="_blank"
                rel="noreferrer"
                className="shrink-0 rounded-md border border-line px-2.5 py-1.5 text-xs font-medium text-text-secondary hover:border-accent hover:text-accent"
              >
                Invoice PDF
              </a>
            </li>
          ))}
        </ul>
        <div className="hidden overflow-x-auto rounded-lg border border-line bg-surface shadow-sm md:block">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-line bg-surface-raised/60 text-left text-xs text-text-muted">
                <th className="px-4 py-2 font-medium">Order</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 text-right font-medium">Units</th>
                <th className="px-4 py-2 text-right font-medium">Total</th>
                <th className="px-4 py-2 font-medium">Date</th>
                <th className="px-4 py-2 font-medium">Invoice</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-line last:border-0">
                  <td className="px-4 py-2 font-mono text-xs text-text-secondary">{o.orderNumber || o.id.slice(-8)}</td>
                  <td className="px-4 py-2">
                    <StatusBadge status={o.status} />
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{o.unitCount}</td>
                  <td className="px-4 py-2 text-right font-medium tabular-nums">{fmtOrderTotal(o)}</td>
                  <td className="px-4 py-2 text-xs text-text-muted">{new Date(o.createdAt).toLocaleDateString()}</td>
                  <td className="px-4 py-2">
                    <a
                      href={invoiceUrl(o.id)}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs font-medium text-accent hover:underline"
                    >
                      PDF
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
      {!loading && <Pager page={page} pageSize={PAGE_SIZE} total={total} onPage={setPage} />}
    </>
  );
}
